/** Espelha `LETTER_SIZES` de `apps/surplus-api/src/core/size_equivalence.py`,
 *  na ordem da escada (XPP é a menor, XXG a maior). */
const LETTER_ORDER = ['XPP', 'PP', 'P', 'M', 'G', 'GG', 'XGG', 'EXG', 'XXG'];

/**
 * Grafia alternativa → posição da letra canônica. Espelha `LETTER_ALIASES` de
 * `apps/surplus-api/src/core/size_notation.py`.
 *
 * Em categoria de roupa o backend já entrega `display_grade` canônico, então
 * isto só aparece nas de numeração própria (cinto, sutiã), onde o grade vem
 * cru. Sem o mapa, um chip `S` ou `EG` cairia no fim da lista, depois dos
 * números.
 */
const ALIAS_TO_CANONICAL: Record<string, string> = {
  EP: 'PP',
  XG: 'XGG',
  EG: 'XGG',
  G1: 'XGG',
  XG1: 'XGG',
  EGG: 'XXG',
  G2: 'XXG',
  G3: 'XXG',
  G4: 'XXG',
  XG2: 'XXG',
  XG3: 'XXG',
  XS: 'PP',
  S: 'P',
  L: 'G',
  XL: 'GG',
  '1X': 'GG',
  '1XL': 'GG',
  XXL: 'XGG',
  '2X': 'XGG',
  '2XL': 'XGG',
  '3X': 'XGG',
  '3XL': 'XGG',
};

const LETTER_INDEX = new Map<string, number>(
  LETTER_ORDER.map((letter, idx) => [letter, idx])
);

for (const [alias, canonical] of Object.entries(ALIAS_TO_CANONICAL)) {
  LETTER_INDEX.set(alias, LETTER_INDEX.get(canonical) as number);
}

export const MIN_LOT_SIZE = 20;

function sortKey(grade: string): [number, number, string] {
  const normalized = grade.trim().toUpperCase();
  const letterIdx = LETTER_INDEX.get(normalized);
  if (letterIdx !== undefined) return [0, letterIdx, normalized];
  const asNumber = Number(normalized);
  if (Number.isFinite(asNumber)) return [1, asNumber, normalized];
  return [2, 0, normalized];
}

export function sortGrades(grades: Iterable<string>): string[] {
  return Array.from(grades).sort((a, b) => {
    const [aBucket, aIdx, aRaw] = sortKey(a);
    const [bBucket, bIdx, bRaw] = sortKey(b);
    if (aBucket !== bBucket) return aBucket - bBucket;
    if (aIdx !== bIdx) return aIdx - bIdx;
    return aRaw.localeCompare(bRaw);
  });
}

export const MINIMUM_TOTAL_ITEMS = MIN_LOT_SIZE;

/** Espelha `MAX_EXCLUDED_PRODUCTS` de
 *  `apps/surplus-api/src/services/self_service/constants.py`. O backend valida
 *  o teto com `max_length`; ultrapassá-lo derruba todos os endpoints da sessão
 *  em 422, então o cliente precisa ser barrado antes. As duas pontas andam
 *  juntas. */
export const MAX_EXCLUDED_PRODUCTS = 200;

/** Espelha `MAX_INCLUDED_PRODUCTS` do mesmo `constants.py`. Muito menor que o
 *  teto das exclusões de propósito: um lote de mínimo 20 peças, curado à mão,
 *  não passa disso — e 200 não limitaria nada num lote de 20. A armadilha é a
 *  mesma: o backend valida com `max_length`, e passar do teto faz preview,
 *  save, listagem e finalize responderem 422 **juntos**, travando a tela sem
 *  explicação. O cliente precisa ser barrado antes. */
export const MAX_INCLUDED_PRODUCTS = 50;
